"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, PhoneCall, ShieldCheck, Wrench, ZapOff, Flame, Volume2, Droplets, ArrowRight, Clock } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const commonIssues = [{
  id: 1,
  title: "Humming Sound, Won't Spin",
  cause: "Jammed flywheel or foreign obstruction inside the grinding chamber.",
  solution: "Manual release and clearing of blades with electrical reset check.",
  urgency: "Medium Priority"
}, {
  id: 2,
  title: "Total Loss of Power",
  cause: "Tripped internal thermal overload, faulty switch, or failed motor.",
  solution: "Circuit testing, internal reset mechanism repair, or power wiring fix.",
  urgency: "Standard Service"
}, {
  id: 3,
  title: "Active Under-Sink Leaks",
  cause: "Worn flange gasket, loose discharge pipe connection, or cracked housing.",
  solution: "Seal replacement, plumbing re-tightening, or complete unit upgrade.",
  urgency: "Emergency"
}, {
  id: 4,
  title: "Slow Draining or Backed-Up Sink",
  cause: "Food waste build-up in drain trap or garbage disposal discharge line.",
  solution: "Line augering, trap disassembly, and high-capacity clearing.",
  urgency: "High Priority"
}, {
  id: 5,
  title: "Persistent Foul Odors",
  cause: "Decaying food particles trapped beneath the splash guard or chamber.",
  solution: "Deep sanitary flush, splash guard replacement, and odor neutralisation.",
  urgency: "Standard Service"
}, {
  id: 6,
  title: "Loud Screeching / Metal Grinding",
  cause: "Worn motor bearings or metal/bone debris wedged in cutters.",
  solution: "Immediate chamber extraction or motor assembly replacement.",
  urgency: "Emergency"
}];
const serviceHighlights = [{
  id: 1,
  title: "Same-Day Emergency Dispatch",
  description: "Rapid mobile vans fully stocked with replacement parts and top brand units in Jonesboro."
}, {
  id: 2,
  title: "Honest Repair vs. Replace Advice",
  description: "We inspect your unit first and only recommend replacement when a repair is no longer safe or cost-effective."
}, {
  id: 3,
  title: "Licensed & Insured Master Plumbers",
  description: "Fully certified technicians ensuring proper electrical safety and watertight sink connections."
}];
export default function GarbageDisposal() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="garbagedisposalservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header Section */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold text-xs tracking-wider uppercase">
            Kitchen Plumbing Solutions
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Garbage Disposal Repair & Replacement in Jonesboro
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">
            A malfunctioning kitchen disposal leads to messy clogs, water damage, and foul odors. Jonesboro Emergency Plumber delivers 24/7 fast diagnostics, jam clearance, and complete new unit installations.
          </p>
        </div>

        {/* Overview & Quick Action Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-16">
          {/* Main Overview Card */}
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={fadeUpVariant} className="lg:col-span-7 flex flex-col justify-between">
            <Card className="h-full border border-border shadow-sm flex flex-col justify-between">
              <CardHeader>
                <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                  <Wrench className="size-4" />
                  <span>Professional Diagnostic Care</span>
                </div>
                <CardTitle className="text-2xl font-bold font-default text-foreground mt-1">
                  Restoring Smooth Kitchen Drainage Fast
                </CardTitle>
                <CardDescription className="text-muted-foreground text-base">
                  Garbage disposals operate using high-speed impellers and a direct tie-in to your home electrical and drainage systems. When a breakdown happens, DIY fixes can present electrical shock risks or blade hazards.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm md:text-base text-foreground leading-relaxed">
                  Our licensed technicians inspect the motor, reset switch, seal integrity, and connecting waste pipe to accurately pinpoint why your disposal is jamming, leaking, or failing to activate. We carry heavy-duty replacement units on every truck for immediate on-the-spot replacements.
                </p>

                <div className="pt-2 space-y-3">
                  {serviceHighlights.map((highlight, index) => <div key={highlight.id} data-index={index} className="flex items-start gap-3 rounded-lg bg-secondary/50 p-3 border border-border/50">
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-sm text-foreground">
                          {highlight.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {highlight.description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </CardContent>
              <div className="p-6 pt-0 mt-auto">
                <Separator className="mb-6" />
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                    <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>Call for Emergency Repair</span>
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                    <Link to="/contact/" className="flex items-center justify-center gap-2">
                      <span>Request Service Estimate</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Side Image / Safety Alert Card */}
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={fadeUpVariant} className="lg:col-span-5 flex flex-col gap-6">
            {/* Visual Media Card */}
            <div className="relative overflow-hidden rounded-xl border border-border bg-muted group aspect-video sm:aspect-4/3 lg:aspect-auto lg:h-72">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/kitchen_sink_disposal_repair.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/kitchen_sink_disposal_repair.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/kitchen_sink_disposal_repair.png-wvc-sizes" alt="Plumber repairing a garbage disposal under kitchen sink" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent flex items-end p-6">
                <div className="text-primary-foreground">
                  <Badge variant="outline" className="border-primary-foreground/40 bg-primary/40 text-primary-foreground mb-1 text-xs">
                    24/7 Availability
                  </Badge>
                  <p className="font-bold text-lg font-default leading-tight">
                    Emergency Kitchen Plumbing in Jonesboro, GA
                  </p>
                </div>
              </div>
            </div>

            {/* Crucial Safety Warning Box */}
            <Alert className="border-destructive/30 bg-destructive/5 text-foreground flex-1 flex flex-col justify-center">
              <AlertTriangle className="size-5 text-destructive shrink-0" />
              <div>
                <AlertTitle className="text-destructive font-bold text-sm">
                  Crucial Safety Warning
                </AlertTitle>
                <AlertDescription className="text-xs md:text-sm text-muted-foreground mt-1 leading-relaxed">
                  Never insert your bare hands into a garbage disposal hopper, even if the wall switch is turned off. If your disposal hums and refuses to rotate, turn off the switch immediately to avoid burning out the motor or creating an electrical fire risk.
                </AlertDescription>
              </div>
            </Alert>
          </motion.div>
        </div>

        {/* Common Issues Section */}
        <div className="mt-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <span className="text-xs uppercase font-semibold text-primary tracking-wider">Troubleshooting Guide</span>
              <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground mt-1">
                Common Garbage Disposal Issues We Fix
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Check what your unit is experiencing. Our certified plumbing specialists carry direct OEM parts to solve each problem on the spot.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commonIssues.map((issue, index) => <motion.div key={issue.id} data-index={index} initial="hidden" whileInView="show" viewport={{
            once: true
          }} variants={fadeUpVariant}>
                <Card className="h-full border border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="p-2 rounded-lg bg-secondary text-primary">
                        {issue.id === 1 && <Volume2 className="size-5" />}
                        {issue.id === 2 && <ZapOff className="size-5" />}
                        {issue.id === 3 && <Droplets className="size-5" />}
                        {issue.id === 4 && <AlertTriangle className="size-5" />}
                        {issue.id === 5 && <Flame className="size-5" />}
                        {issue.id === 6 && <Wrench className="size-5" />}
                      </div>
                      <Badge variant={issue.urgency === "Emergency" ? "destructive" : issue.urgency === "High Priority" ? "default" : "secondary"} className="text-[10px] uppercase font-semibold">
                        {issue.urgency}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-bold font-default text-foreground">
                      {issue.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div>
                      <span className="font-semibold text-xs text-muted-foreground uppercase tracking-wider block">
                        Probable Cause
                      </span>
                      <p className="text-foreground mt-0.5 leading-snug">
                        {issue.cause}
                      </p>
                    </div>
                    <div>
                      <span className="font-semibold text-xs text-muted-foreground uppercase tracking-wider block">
                        Our Solution
                      </span>
                      <p className="text-muted-foreground mt-0.5 leading-snug">
                        {issue.solution}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Emergency Callout Banner */}
        <div className="mt-12 rounded-2xl bg-secondary p-8 md:p-10 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-primary text-primary-foreground shrink-0 hidden sm:block">
              <Clock className="size-6" />
            </div>
            <div>
              <h4 className="text-xl md:text-2xl font-bold font-default text-foreground">
                Need Fast Garbage Disposal Service in Jonesboro?
              </h4>
              <p className="text-sm md:text-base text-muted-foreground mt-1 max-w-2xl">
                Do not let a broken disposal shut down your kitchen. We offer 24/7 emergency dispatch and guaranteed transparent pricing.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a href="tel:4708479076" className="flex items-center gap-2">
                <PhoneCall className="size-4" />
                <span>Call Now: (470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/problems/">
                <span>View All Problems</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}